import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/clinic/Navbar";
import Footer from "@/components/clinic/Footer";
import { applyJsonLd, applySeo, SITE_URL } from "@/lib/seo";

const Privacy = () => {
  useEffect(() => {
    applySeo({
      title: "Privacy Policy — Blepharoplasty London | Dr Vahe Clinic",
      description:
        "How Dr Vahe Clinic, a private blepharoplasty practice in central London, collects, uses and protects patient information across the UK.",
      path: "/privacy",
      ogType: "article",
    });

    const cleanup = applyJsonLd("breadcrumbs-privacy", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Privacy Policy", item: `${SITE_URL}/privacy` },
      ],
    });

    return () => cleanup();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <article className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
          <Link
            to="/"
            className="text-[10px] tracking-[0.3em] uppercase text-accent hover:text-accent/70 transition-colors"
          >
            ← Back to clinic
          </Link>

          <header className="mt-8 mb-12">
            <span className="text-[10px] tracking-[0.35em] uppercase text-accent font-medium">
              Legal
            </span>
            <h1 className="font-display text-4xl md:text-5xl mt-4 text-foreground">
              Privacy <span className="italic">Policy</span>
            </h1>
            <p className="mt-4 text-xs tracking-[0.2em] uppercase text-muted-foreground">
              Last updated: April 2026
            </p>
          </header>

          <div className="space-y-10 text-foreground/85 font-light leading-relaxed">
            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">1. Who we are</h2>
              <p>
                Dr Vahe Clinic ("we", "our", "us") is a private medical practice based in central
                London. We are the data controller responsible for your personal information. For
                any privacy enquiries, please contact us via the details on our main site.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">2. Information we collect</h2>
              <p>
                We collect information you provide when enquiring about or undergoing treatment,
                including your name, contact details, date of birth, medical history, photographs
                where clinically relevant, and any correspondence with our team. We may also
                collect basic technical information such as your IP address and browser type when
                you use this website.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">3. How we use your information</h2>
              <p>
                Your information is used to arrange and deliver your consultation and treatment,
                maintain accurate medical records, communicate with you about appointments and
                aftercare, comply with our regulatory and legal obligations, and — where you have
                agreed — to send relevant clinic updates.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">4. Lawful basis</h2>
              <p>
                We process your data on the basis of the contract for your care, our legal
                obligations as a healthcare provider, our legitimate interests in operating the
                practice, and — for sensitive medical information — your explicit consent given at
                the point of enquiry or consultation.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">5. Sharing your information</h2>
              <p>
                We do not sell your data. We share information only with those who genuinely need
                it to deliver your care, including our clinical team, the surgical facility,
                anaesthetists, accredited laboratories, and our secure booking and records
                providers. We may disclose information where required by law or regulator.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">6. Retention</h2>
              <p>
                Medical records are retained in line with NHS and professional guidance, generally
                for a minimum of eight years after your last interaction with the practice. Other
                enquiry data is held only for as long as necessary.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">7. Your rights</h2>
              <p>
                You have the right to access, correct, restrict or request deletion of your
                personal information, and to withdraw consent for marketing at any time. To
                exercise any right, please contact us. You may also lodge a complaint with the
                Information Commissioner's Office (ICO).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">8. Cookies</h2>
              <p>
                This website uses only essential cookies required for it to function. We do not
                use advertising cookies on this page.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="font-display text-2xl text-foreground">
                9. Where we operate — London &amp; the UK
              </h2>
              <p>
                Dr Vahe Clinic is a private <strong>blepharoplasty practice in central London</strong>,
                serving patients across Mayfair, Marylebone, the Harley Street medical district,
                Knightsbridge, Chelsea, Kensington, the City of London and the wider Greater
                London area, as well as visiting patients from across the United Kingdom and
                overseas. All personal and medical information is processed in the United Kingdom
                in accordance with UK GDPR and the Data Protection Act 2018.
              </p>
            </section>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default Privacy;
